import { lazy, Suspense, useEffect } from 'react';
import { AgreementPage } from './components/auth/AgreementPage';
import { PasswordRotationPage } from './components/auth/PasswordRotationPage';
import { ToastHost } from './components/ToastHost';
import { Spinner } from './components/ui/Spinner';
import { useSession } from './features/auth/useAuth';
import { useT } from './i18n/useT';
import { authPathWithNext, isPublicRoute, normalizePath, postLoginPath } from './routes/paths';
import { navigate, usePathname, useRoute, useSearch } from './routes/router';
import { useCanonical } from './site/useCanonical';

/**
 * Uygulama kökü — yalnızca "hangi kabuk" kararını verir.
 *
 * Üç yüzey var ve ikisi ayrı parçalara bölünmüş durumda:
 *
 *   tanıtım sitesi (`site/SiteApp`)  → herkese açık, kimlik gerekmez
 *   giriş ekranı                     → sitenin kabuğu içinde, `/giris`
 *   panel (`panel/PanelApp`)         → kimlik zorunlu, `/panel`
 *
 * ## Site oturum sorgusunu BEKLEMEZ
 *
 * Ziyaretçinin ana sayfayı görmesi için `/auth/me` yanıtının dönmesi
 * gerekmiyor. Beklenseydi her ilk açılış bir ağ gidiş-dönüşü kadar geç
 * boyanırdı ve bu, sitenin en çok bakılan ekranında doğrudan bir LCP kaybı
 * olurdu. Oturum yalnızca sağ üstteki düğmenin yazısını değiştiriyor ve
 * cevap geldiğinde kendiliğinden güncelleniyor.
 *
 * Panel ise beklemek ZORUNDA: kimliği çözülmeden render edilen bir panel,
 * her isteği 401 dönen boş ekranlar üretirdi.
 */

const SiteApp = lazy(() => import('./site/SiteApp').then((module) => ({ default: module.SiteApp })));
const PanelApp = lazy(() =>
  import('./panel/PanelApp').then((module) => ({ default: module.PanelApp })),
);

export default function App() {
  const route = useRoute();
  const pathname = usePathname();
  const search = useSearch();
  const { user, isLoading, isAuthenticated } = useSession();

  const isAuthRoute = route === 'AUTH';
  const isPanelRoute = route === 'PANEL';

  /* Giriş yapmış kullanıcı giriş ekranında durmamalı: `?next=` varsa oraya,
     yoksa panele. Şifre rotasyonu bekleyen de panele gider — rotasyon ekranı
     ORADA çiziliyor (aşağıdaki `user.mustChangePassword` dalı). Önceki hâli
     bu kullanıcıyı "hariç" tutup giriş ekranında bırakıyordu ve onu rotasyon
     ekranına götüren başka bir yol yoktu; karar artık `postLoginPath` içinde
     ve testli. */
  useEffect(() => {
    if (!isAuthRoute) return;
    if (!isAuthenticated || user === null) return;

    /* Sözleşme bekleyen üye de panele gider: kapı ekranı orada çiziliyor ve
       `?next=` ile siteye dönen yeni üye sözleşmeyi hiç görmezdi. */
    navigate(postLoginPath(search, user.mustChangePassword || user.agreementRequired), {
      replace: true,
    });
  }, [isAuthRoute, isAuthenticated, user, search]);

  /* Kimliksiz panel erişimi giriş ekranına düşer ve nereye gitmek istediğini
     taşır. Bu bir GÜVENLİK sınırı değil — yetkiyi her uçta `requireAuth`
     uyguluyor; buradaki tek amaç kullanıcıyı boş bir ekranla baş başa
     bırakmamak. */
  useEffect(() => {
    if (!isPanelRoute) return;
    if (isLoading || isAuthenticated) return;

    navigate(authPathWithNext(pathname), { replace: true });
  }, [isPanelRoute, isLoading, isAuthenticated, pathname]);

  const activePath = normalizePath(pathname);

  useCanonical(activePath, isPublicRoute(route));

  if (isPublicRoute(route) || isAuthRoute) {
    return (
      <>
        <Suspense fallback={<FullScreenSpinner />}>
          <SiteApp route={route} activePath={activePath} isAuthenticated={isAuthenticated} />
        </Suspense>
        <ToastHost />
      </>
    );
  }

  // Buradan sonrası yalnızca panel.
  if (isLoading || !isAuthenticated || user === null) {
    return (
      <>
        <FullScreenSpinner />
        <ToastHost />
      </>
    );
  }

  /* Geçici şifre henüz döndürülmedi: hiçbir panel render edilmez.
     Bu bir görsel tercih DEĞİL, sunucudaki kilidin karşılığıdır — `requireAuth`
     bu kullanıcıya /auth/me ve /auth/me/password dışında 403 döner, dolayısıyla
     uygulamayı göstermek boş ekranlar ve anlamsız hatalar üretirdi. */
  if (user.mustChangePassword) {
    return (
      <>
        <PasswordRotationPage />
        <ToastHost />
      </>
    );
  }

  /* Üyelik sözleşmesi imzalanmadı: panel render edilmez. Sunucu bu üyeye
     /auth/me, /auth/me/agreement, /auth/me/password ve /auth/logout dışında
     403 AGREEMENT_REQUIRED döner — şifre kapısıyla aynı mantık ve ondan
     SONRA: geçici şifreyle açılan üye önce şifresini, sonra sözleşmeyi. */
  if (user.agreementRequired) {
    return (
      <>
        <AgreementPage user={user} />
        <ToastHost />
      </>
    );
  }

  return (
    <>
      <Suspense fallback={<FullScreenSpinner />}>
        <PanelApp user={user} />
      </Suspense>
      <ToastHost />
    </>
  );
}

function FullScreenSpinner() {
  const t = useT();

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas">
      <Spinner className="h-8 w-8 text-ink-3" label={t.a11y.checkingSession} />
    </div>
  );
}
